import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Building2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Pagina "Intestazione aziendale" — `/admin/impostazioni/intestazione`.
 *
 * Modello dati allineato a `CompanyHeader` nel backend
 * (POST/PUT/GET /api/company-header).
 */
export default function CompanyHeaderPage() {
  const [form, setForm] = useState({
    ragione_sociale: '', indirizzo: '', cap: '', citta: '', provincia: '',
    partita_iva: '', codice_fiscale: '', telefono: '', email: '', pec: '',
    sito_web: '', iban: '', banca: '', logo_url: '', nota_piede: '', rea: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/company-header`, { withCredentials: true })
      .then(r => setForm(prev => ({ ...prev, ...r.data })))
      .catch(() => toast.error('Impossibile caricare intestazione'))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/company-header`, form, { withCredentials: true });
      toast.success('Intestazione aggiornata');
    } catch {
      toast.error('Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="company-header-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Building2 className="h-6 w-6" /> Intestazione aziendale</h1>
        <p className="text-sm text-slate-500 mt-1">Questi dati appaiono in alto su tutti i preventivi, fatture, DDT e conteggi PDF.</p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="border-slate-200">
          <CardContent className="p-6 space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Dati identificativi</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Ragione sociale</Label>
                  <Input value={form.ragione_sociale} onChange={set('ragione_sociale')} placeholder="es. CR Impianti Elettrici S.r.l." data-testid="header-ragione-sociale" />
                </div>
                <div><Label>P.IVA</Label><Input value={form.partita_iva} onChange={set('partita_iva')} data-testid="header-piva" /></div>
                <div><Label>Codice Fiscale</Label><Input value={form.codice_fiscale} onChange={set('codice_fiscale')} /></div>
                <div><Label>REA</Label><Input value={form.rea} onChange={set('rea')} placeholder="numero REA camera commercio" /></div>
                <div><Label>Logo URL</Label><Input value={form.logo_url} onChange={set('logo_url')} placeholder="https://..." /></div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Sede</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2"><Label>Indirizzo</Label><Input value={form.indirizzo} onChange={set('indirizzo')} placeholder="Via, n. civico" /></div>
                <div><Label>CAP</Label><Input value={form.cap} onChange={set('cap')} /></div>
                <div><Label>Città</Label><Input value={form.citta} onChange={set('citta')} /></div>
                <div><Label>Provincia</Label><Input value={form.provincia} onChange={set('provincia')} placeholder="es. MI" /></div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Contatti</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Telefono</Label><Input value={form.telefono} onChange={set('telefono')} /></div>
                <div><Label>Email</Label><Input value={form.email} onChange={set('email')} type="email" /></div>
                <div><Label>PEC</Label><Input value={form.pec} onChange={set('pec')} type="email" /></div>
                <div><Label>Sito Web</Label><Input value={form.sito_web} onChange={set('sito_web')} /></div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Dati bancari</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>IBAN</Label><Input value={form.iban} onChange={set('iban')} className="font-mono" /></div>
                <div><Label>Banca</Label><Input value={form.banca} onChange={set('banca')} /></div>
              </div>
            </div>

            <div>
              <Label>Nota a piè di pagina</Label>
              <Textarea value={form.nota_piede} onChange={set('nota_piede')} rows={3} placeholder="es. Condizioni di pagamento, garanzie, footer legale" />
              <p className="text-xs text-slate-500 mt-1">Visualizzato in fondo a tutti i documenti generati.</p>
            </div>

            <div className="flex justify-end pt-2 border-t">
              <Button type="submit" disabled={saving} className="bg-slate-900 text-white" data-testid="save-header">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Salvataggio…' : 'Salva intestazione'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
